import Icon, { IconName, IconScale } from '@ben/orion/icon';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import type { ChangeEvent, FormEventHandler } from 'react';
import { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import type { ChatMessage } from './types';

export interface AssistantChatProps {
  initialChatMessages?: ChatMessage[];
  userActionRequired: boolean;
  onSubmitChat(history: ChatMessage[], userMessage?: string): void;
}

const AssistantChat = ({
  userActionRequired,
  onSubmitChat,
  initialChatMessages,
}: AssistantChatProps) => {
  const userChatBox = useRef<HTMLDivElement | null>(null);
  const [userMessage, setUserMessage] = useState<string | undefined>();
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>(
    initialChatMessages ?? []
  );

  useEffect(() => {
    if (initialChatMessages) {
      setChatMessages(initialChatMessages);
    }
  }, [initialChatMessages]);

  const updateUserInput: FormEventHandler<HTMLTextAreaElement> = (
    event: ChangeEvent<HTMLTextAreaElement>
  ) => {
    // grow the textarea as the user types
    event.target.style.height = 'auto';
    event.target.style.height = `${event.target.scrollHeight}px`;

    setUserMessage(event.target.value);
  };

  useEffect(() => {
    if (userChatBox?.current) {
      userChatBox.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [userChatBox]);

  const submitUserMessage = () => {
    let newChatMessages = chatMessages;

    if (userMessage) {
      newChatMessages = [
        ...chatMessages,
        { message: userMessage, isUser: true },
      ];
      setChatMessages(newChatMessages);
    }

    onSubmitChat(newChatMessages, userMessage);

    setUserMessage(undefined);
  };

  return (
    <>
      {chatMessages.map((message, index) => (
        <ChatMessageContainer
          key={`chat-${index}`}
          className={message.isUser ? 'sent' : 'received'}
        >
          <Typography variant={TypographyVariant.bodySm1}>
            {message.message}
          </Typography>
        </ChatMessageContainer>
      ))}
      {userActionRequired && (
        <ChatMessageContainer className={'sent'} ref={userChatBox}>
          <UserChatInput
            onInput={updateUserInput}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                submitUserMessage();
              }
            }}
            placeholder={'Type your message here...'}
          />
          <SendButton onClick={submitUserMessage}>
            <Icon
              name={IconName.ArrowRight}
              fillcolor={'black'}
              scale={IconScale.XS}
            />
          </SendButton>
        </ChatMessageContainer>
      )}
    </>
  );
};

export default AssistantChat;

const ChatMessageContainer = styled.div`
  ${({ theme }) => css`
    background-color: ${theme.palette.neutral[50]};
    border-radius: 10px;
    padding: 10px;
    margin: 10px 0;
    word-wrap: break-word;
    box-shadow: 0 2px 4px ${theme.palette.black(0.1)};
    flex-grow: 1;
    flex-shrink: 1;
    display: flex;
    flex-direction: column;
    &.sent {
      margin-left: 20%;
      background-color: ${theme.palette.secondary.light};
      align-self: flex-end;
    }

    &.received {
      margin-right: 20%;

      background-color: ${theme.palette.primary.light};
      align-self: flex-start;
    }
  `}
`;

const UserChatInput = styled.textarea`
  ${({ theme }) => css`
    background-color: transparent;
    padding: 8px;
    font-size: 0.75rem;
    color: ${theme.palette.neutral[100]};
    width: 100%;
    box-shadow: none;
    outline: none;
    resize: none;
    height: auto;
    overflow: hidden;
    border: none;
  `}
`;

const SendButton = styled.button`
  ${({ theme }) => css`
    background-color: transparent;
    color: ${theme.palette.neutral['00']};
    border: none;
    cursor: pointer;
    align-self: flex-end;
  `}
`;
